import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { Bucket } from '../types';
import { UploadManager, UploadState } from '../services/UploadManager';

export interface FileProgress {
  file: File;
  state: UploadState;
}

export interface UploadBatch {
  id: string;
  bucketName: string;
  bucketDisplayName: string;
  totalFiles: number;
  completedCount: number;
  failedCount: number;
  isUploading: boolean;
  error: string;
  limitExceeded: boolean;
  fileProgress: Map<string, FileProgress>;
  startedAt: number;
}

interface UploadContextValue {
  batches: UploadBatch[];
  startUpload: (files: File[], bucket: Bucket, onComplete?: () => void) => void;
  cancelBatch: (batchId: string) => void;
  dismissBatch: (batchId: string) => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

const MAX_CONCURRENT = 10;

const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [batches, setBatches] = useState<UploadBatch[]>([]);
  const managersRef = useRef<Map<string, Map<string, UploadManager>>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const startUpload = useCallback((files: File[], bucket: Bucket, onComplete?: () => void) => {
    const batchId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const abortController = new AbortController();
    abortControllersRef.current.set(batchId, abortController);

    const displayName = bucket.displayName || bucket.name;

    setBatches(prev => [...prev, {
      id: batchId,
      bucketName: bucket.name,
      bucketDisplayName: displayName,
      totalFiles: files.length,
      completedCount: 0,
      failedCount: 0,
      isUploading: true,
      error: '',
      limitExceeded: false,
      fileProgress: new Map(),
      startedAt: Date.now(),
    }]);

    const managers = new Map<string, UploadManager>();
    managersRef.current.set(batchId, managers);

    (async () => {
      const results = { completed: 0, failed: 0 };

      try {
        const queue = [...files];
        const inProgress = new Set<Promise<void>>();

        while (queue.length > 0 || inProgress.size > 0) {
          if (abortController.signal.aborted) throw new Error('Upload cancelled');

          while (inProgress.size < MAX_CONCURRENT && queue.length > 0) {
            const file = queue.shift()!;
            const key = fileKey(file);

            const manager = new UploadManager(
              file,
              bucket.name,
              (state: UploadState) => {
                setBatches(prev => prev.map(b => {
                  if (b.id !== batchId) return b;
                  const newMap = new Map(b.fileProgress);
                  newMap.set(key, { file, state });
                  return { ...b, fileProgress: newMap };
                }));
              },
              undefined
            );
            managers.set(key, manager);

            const promise = manager.upload()
              .then(() => {
                results.completed++;
                setBatches(prev => prev.map(b =>
                  b.id === batchId ? { ...b, completedCount: results.completed } : b
                ));
              })
              .catch((err) => {
                if (err.name !== 'AbortError' && !err.message?.includes('cancelled')) {
                  if ((err as any).isStorageLimitError) {
                    setBatches(prev => prev.map(b =>
                      b.id === batchId ? { ...b, isUploading: false, limitExceeded: true } : b
                    ));
                    abortController.abort();
                  } else {
                    results.failed++;
                    setBatches(prev => prev.map(b =>
                      b.id === batchId ? { ...b, failedCount: results.failed } : b
                    ));
                  }
                }
              });

            inProgress.add(promise);
            promise.finally(() => inProgress.delete(promise));
          }

          if (inProgress.size > 0) await Promise.race(inProgress);
        }

        if (results.failed === 0) {
          setBatches(prev => prev.map(b =>
            b.id === batchId ? { ...b, isUploading: false } : b
          ));
          onComplete?.();
          setTimeout(() => {
            setBatches(prev => prev.filter(b => b.id !== batchId));
          }, 4000);
        } else {
          setBatches(prev => prev.map(b =>
            b.id === batchId ? {
              ...b,
              isUploading: false,
              error: `${results.failed} file(s) failed. ${results.completed} succeeded.`,
            } : b
          ));
          if (results.completed > 0) onComplete?.();
        }
      } catch (err: any) {
        const msg = (err.name === 'AbortError' || err.message?.includes('cancelled'))
          ? 'Upload cancelled'
          : (err.message || 'Upload failed');
        setBatches(prev => prev.map(b =>
          b.id === batchId ? { ...b, isUploading: false, error: msg } : b
        ));
      } finally {
        abortControllersRef.current.delete(batchId);
        managersRef.current.delete(batchId);
      }
    })();
  }, []);

  const cancelBatch = useCallback(async (batchId: string) => {
    abortControllersRef.current.get(batchId)?.abort();
    const managers = managersRef.current.get(batchId);
    if (managers) {
      await Promise.all(Array.from(managers.values()).map(m => m.cancel()));
    }
  }, []);

  const dismissBatch = useCallback((batchId: string) => {
    setBatches(prev => prev.filter(b => b.id !== batchId));
  }, []);

  return (
    <UploadContext.Provider value={{ batches, startUpload, cancelBatch, dismissBatch }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUploadContext = () => {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploadContext must be used within UploadProvider');
  return ctx;
};
